import { Component, Input, OnChanges, SimpleChanges, ElementRef, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-pdf-thumbnail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    <canvas #canvas [class.hidden]="!loaded" [style.width.px]="displayWidth"></canvas>
    @if (!loaded && !error) {
      <div class="placeholder">Loading...</div>
    }
    @if (error) {
      <div class="placeholder error">Preview unavailable</div>
    }
  `,
  styles: [`
    :host { display: block; }
    canvas { border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.12); }
    canvas.hidden { display: none; }
    .placeholder {
      display: flex; align-items: center; justify-content: center;
      width: 100%; min-height: 160px;
      background: #f0f0f0; border-radius: 6px;
      font-size: 0.8rem; color: #888;
    }
    .placeholder.error { color: #999; }
  `]
})
export class PdfThumbnailComponent implements OnChanges {
  @Input() file: File | null = null;
  @Input() displayWidth = 180;

  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  private cdr = inject(ChangeDetectorRef);

  loaded = false;
  error = false;

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['file'] && this.file) {
      await this.renderPage1(this.file);
    }
  }

  private async renderPage1(file: File): Promise<void> {
    this.loaded = false;
    this.error = false;
    this.cdr.markForCheck();

    if (!file.type.includes('pdf')) {
      this.error = true;
      this.cdr.markForCheck();
      return;
    }

    try {
      const pdfjsLib: any = await import('pdfjs-dist');

      // Worker must be hosted somewhere reachable from the browser. Use the CDN
      // mirror at the version installed in package.json. Falls back to 5.6.205
      // (our current pin) if the runtime version is not exposed.
      const version = pdfjsLib.version || '5.6.205';
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.mjs`;

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1);

      const scale = this.displayWidth / page.getViewport({ scale: 1 }).width;
      const viewport = page.getViewport({ scale });

      const canvas = this.canvasRef.nativeElement;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) { this.error = true; this.cdr.markForCheck(); return; }

      await page.render({ canvasContext: ctx, viewport }).promise;
      this.loaded = true;
      this.cdr.markForCheck();
    } catch (err) {
      console.warn('[PdfThumbnail] Failed to render:', err);
      this.error = true;
      this.cdr.markForCheck();
    }
  }
}
